import React, {useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {
  getAnchorPriceNightlyPerRoom,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'
import {RootState} from 'store'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {getRoomOfferPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {
  RoomWithoutOffers,
  SplitBookingDates
} from '@vio/core/src/room/types/room'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {Price} from '@vio/shared/src/search/Price'

import {RoomOfferPriceDataId} from '../RoomOfferPrice/constants'
import {PriceDetailsButtonWithTax} from '../RoomOfferPrice/RoomOfferPrice'
import {RoomsTopOfferPriceBreakdownModal} from './RoomsTopOfferPriceBreakdownModal'

type Props = {
  hotelId: string
  prices: OfferPrice[]
  splitBookingRooms: RoomWithoutOffers[]
  splitBookingDates: SplitBookingDates
  isOfferSoldOut?: boolean
  // Hide the secondary price-details line (total/nightly breakdown button).
  hidePriceDetails?: boolean
  // Left-align the price instead of the default right-alignment - used in
  // space-constrained layouts like the AP sticky bottom bar.
  isCompact?: boolean
}

const BaselineRow = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: start;
  white-space: nowrap;
`

const PostFix = styled(Text)<{isOfferSoldOut?: boolean}>`
  margin-left: ${cssTheme.layout.spacing.s200};
  color: ${({isOfferSoldOut}) =>
    isOfferSoldOut ? cssTheme.colors.content.neutral.disabled : null};
`

const SecondaryPriceWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  white-space: pre-wrap;
  justify-content: flex-end;
`

const BasePriceRow = styled.div<{isCompact?: boolean}>`
  display: flex;
  white-space: nowrap;
  max-width: 140px;
  align-items: baseline;
  justify-content: ${({isCompact}) => (isCompact ? 'flex-start' : 'flex-end')};
  gap: ${cssTheme.layout.spacing.s200};
`

const roundingStrategy = getRoundingStrategy()

const postFixMessages = defineMessages({
  total: {id: 'price.totalV2', defaultMessage: 'total'},
  perNight: {id: 'perNight', defaultMessage: '/ night'}
})

export const RoomsTopOfferPrice = ({
  hotelId,
  prices,
  splitBookingRooms,
  splitBookingDates,
  isOfferSoldOut = false,
  hidePriceDetails = false,
  isCompact = false
}: Props) => {
  const [openPriceBreakdownOverlay, setOpenPriceBreakdownOverlay] =
    useState(false)

  const {formatMessage} = useIntl()
  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })
  const anchorPrice = useSelector((state: RootState) =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const showTotalPrices = useSelector(getShowTotalPrices)

  const onClickPriceDetails = () => {
    setOpenPriceBreakdownOverlay(true)
  }

  // A split booking is one combined price, so it is never multiplied by rooms
  const roomPrices = getRoomOfferPrices(prices, {
    numberOfRooms: 1,
    anchorNightly: anchorPrice,
    anchorTotal: anchorPriceTotal
  })
  if (!roomPrices) return null

  const {currencyCode} = roomPrices
  const mainPrice = showTotalPrices ? roomPrices.total : roomPrices.nightly
  const detailsPrice = showTotalPrices ? roomPrices.nightly : roomPrices.total

  const mainPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.Total
    : RoomOfferPriceDataId.PerNight

  const detailsPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.PerNight
    : RoomOfferPriceDataId.Total

  const isMainPriceDifferentFromDetailsPrice =
    mainPrice.price !== detailsPrice.price

  const pricePostFix = showTotalPrices
    ? postFixMessages.total
    : postFixMessages.perNight
  const priceDetailPostFix = showTotalPrices
    ? postFixMessages.perNight
    : postFixMessages.total

  const soldOutColor = isOfferSoldOut
    ? cssTheme.colors.content.neutral.disabled
    : null

  const priceDetailsContent = (
    <Text
      tag="div"
      variant="labelS"
      colorPath={isOfferSoldOut ? 'content.neutral.disabled' : null}
    >
      <SecondaryPriceWrapper data-id="splitBooking.totalPrice">
        <Price
          currency={currencyCode}
          value={detailsPrice.price}
          roundingStrategy={roundingStrategy}
          noStyle
          dataId={detailsPriceDataId}
        />
        <BaselineRow>
          &nbsp;
          {formatMessage(priceDetailPostFix)}
        </BaselineRow>
      </SecondaryPriceWrapper>
    </Text>
  )

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        flexWrap="wrap"
        alignItems={isCompact ? 'flex-start' : 'flex-end'}
      >
        <BasePriceRow isCompact={isCompact}>
          {mainPrice.showAnchor ? (
            <Price
              isAnchor
              currency={currencyCode}
              value={mainPrice.anchor}
              roundingStrategy={roundingStrategy}
              style={{color: soldOutColor}}
              dataId="splitBooking.anchorPrice"
            />
          ) : null}

          <Price
            currency={currencyCode}
            value={mainPrice.price}
            roundingStrategy={roundingStrategy}
            style={{color: soldOutColor}}
            size="lg"
            dataId={mainPriceDataId}
          />
          <PostFix variant="bodyS" isOfferSoldOut={isOfferSoldOut}>
            {formatMessage(pricePostFix)}
          </PostFix>
        </BasePriceRow>

        {!hidePriceDetails && isMainPriceDifferentFromDetailsPrice && (
          <PriceDetailsButtonWithTax
            onClick={onClickPriceDetails}
            hotelId={hotelId}
            isOfferSoldOut={isOfferSoldOut}
          >
            {priceDetailsContent}
          </PriceDetailsButtonWithTax>
        )}
      </Box>
      <RoomsTopOfferPriceBreakdownModal
        isOpen={openPriceBreakdownOverlay}
        onClose={() => setOpenPriceBreakdownOverlay(false)}
        prices={prices}
        splitBookingRooms={splitBookingRooms}
        splitBookingDates={splitBookingDates}
      />
    </>
  )
}
